import React, { useEffect, useState, useRef } from 'react';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddIcon, DeleteIcon, PrintIcon } from 'cokitchen-icons';
import EmptyState from './empty-state';
import CreateTableModal from './create-table-modal';
import { QRCodeSVG } from 'qrcode.react';
import useDashboardContext from 'contexts/dashboard.context';
import DeleteModal from 'components/base/delete-modal';
import { handleApiError } from 'utils/functions';
import DarkLogo from 'assets/images/dark-logo.png';
import { apiService } from 'services/base.service';
import { DineInTableType } from 'models/dine-in-table';
import PageText from 'components/base/page-text';
import { toast } from 'react-toastify';
import ReactToPrint from 'react-to-print';
import QrCodePrint from './qr-code-print';
import { ENUM_COKITCHEN_STATUS } from 'utils/enum';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';

const QrCode = () => {
  const [tables, setTables] = useState<DineInTableType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateNewTable, setShowCreateNewTable] = useState(false);
  const [showDeleteTable, setShowDeleteTable] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState({} as DineInTableType);
  const { activeCokitchen } = useDashboardContext();
  const navigate = useNavigate();

  const componentRef = useRef<HTMLDivElement>(null);

  const toggleDelete = (e: React.MouseEvent, table: DineInTableType): void => {
    e.stopPropagation();
    setShowDeleteTable(true);
    setSelectedTable(table);
  };

  const printQrCode = (table: DineInTableType) => {
    setSelectedTable(table);
  };

  const handleDelete = async () => {
    if (!selectedTable) return;
    setDeleteLoading(true);
    try {
      const res = await apiService.delete(
        `dine-in-tables/cokitchens/dine-in-tables/${selectedTable.id}`
      );
      setTables(tables.filter((table) => table.id !== selectedTable.id));
      setShowDeleteTable(false);
      toast(<ToastMessage type='success' message={res.data.message} />);
    } catch (error) {
      handleApiError(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const fectchCokitchenTables = async () => {
    setLoading(true);
    try {
      const res = await apiService.get<{ data: { data: DineInTableType[] } }>('dine-in-tables', {
        params: {
          cokitchen_id: activeCokitchen.id
        }
      });
      setTables(res.data.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fectchCokitchenTables();
  }, []);

  useEffect(() => {
    if (activeCokitchen.cokitchen_type !== ENUM_COKITCHEN_STATUS.DINE_IN) {
      navigate(`${ROUTE_PREFIX.FOOD}/${ROUTE_KEYS.OVERVIEW}`);
    }
  }, [activeCokitchen]);
  return (
    <div>
      <div className='print-only'>
        <QrCodePrint
          link={`https://staging-kiosk.getfoodcourt.com/dine-in/${activeCokitchen.id}/${selectedTable.id}`}
          //   link={`https://kiosk.getfoodcourt.com/dine-in/${activeCokitchen.id}/${selectedTable.id}`}
          ref={componentRef}
          tableNumber={String(selectedTable.number) || ''}
        />
      </div>
      {showCreateNewTable && (
        <CreateTableModal
          show={showCreateNewTable}
          onClose={() => setShowCreateNewTable(false)}
          setTables={setTables}
          tables={tables}
        />
      )}
      {showDeleteTable && (
        <DeleteModal
          type='delete'
          show={showDeleteTable}
          onClose={() => setShowDeleteTable(false)}
          handleDelete={handleDelete}
          title={`Are you sure you want to delete Table ${selectedTable.number}?`}
          description='This action cannot be undone'
          loading={deleteLoading}
        />
      )}
      <div className='flex justify-between'>
        <div>
          <h1 className='text-xl font-semibold'>QR Code</h1>
          <p className='text-sm text-gray-5'>Manage QR codes</p>
        </div>
        <div>
          <Button
            variant='block'
            color='primary'
            size='small'
            onClick={() => setShowCreateNewTable(true)}>
            <AddIcon />
            Create new table
          </Button>
        </div>
      </div>

      {loading ? (
        <PageText text='Tables loading...' />
      ) : tables.length === 0 ? (
        <EmptyState />
      ) : (
        <section className='mt-10 flex flex-wrap gap-x-6 gap-y-8 justify-start'>
          {tables
            .sort((table1, table2) => table1.number - table2.number)
            .map((table) => (
              <section key={table.id} className='rounded-md shadow-md'>
                <div className='w-[260px] h-[250px] relative'>
                  <img
                    src={DarkLogo}
                    width={45}
                    height={45}
                    className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-2'
                  />
                  <QRCodeSVG
                    width={260}
                    height={250}
                    value={`https://staging-kiosk.getfoodcourt.com/dine-in/${activeCokitchen.id}/${table.id}`}
                    // value={`https://kiosk.getfoodcourt.com/dine-in/${activeCokitchen.id}/${table.id}`}
                  />
                </div>
                <div className='flex items-center justify-between p-3'>
                  <p className='font-medium'>Table {table.number}</p>
                  <section className='flex items-center gap-3'>
                    <ReactToPrint
                      onBeforeGetContent={async () => {
                        printQrCode(table);
                      }}
                      trigger={() => (
                        <div
                          className='p-2 rounded-full border-[2px] border-gray-light cursor-pointer bg-[#FAFAFA]'
                          title='Print'
                          role='button'>
                          <PrintIcon className='text-[#858585]' />
                        </div>
                      )}
                      content={() => componentRef.current}
                      documentTitle={`Qr Code Image for Table ${selectedTable.number} in ${activeCokitchen.name}`}
                    />

                    <div
                      className=' p-2 rounded-full border-[2px] border-gray-light cursor-pointer bg-[#FAFAFA]'
                      role='button'
                      onClick={(e) => toggleDelete(e, table)}>
                      <DeleteIcon className='text-[#858585]' />
                    </div>
                  </section>
                </div>
              </section>
            ))}
        </section>
      )}
    </div>
  );
};

export default QrCode;
